/** Option shape as stored: legacy records use `title`, current ones `label`. */
type RawVariableOption = {
  label?: string;
  title?: string;
  value?: unknown;
};

/**
 * Normalize select/multiselect options to `{ label, value }`.
 *
 * Options come from two eras: legacy variable records store the display text as
 * `title`, current ones as `label` (the declared type only admits `label`, so
 * legacy data arrives mis-typed). ChoiceControl renders `label`, so an
 * un-normalized legacy option renders as a BLANK row — the dropdown looks empty
 * and the selected-value summary falls back to the placeholder. Falls back to
 * the raw value so an option is never rendered label-less.
 */
export const normalizeVariableOptions = (
  options: RawVariableOption[] | undefined,
): Array<{ label: string; value: string }> =>
  (options ?? []).map((option) => {
    const value = String(option?.value ?? "");
    return { label: option?.label ?? option?.title ?? value, value };
  });

const decimalPattern = /^-?(?:\d+|\d*\.\d+)$/;

export const isDecimalValue = (value: string): boolean =>
  !value || decimalPattern.test(value);

export const isHttpUrlValue = (value: string): boolean => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const toDateValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const fromDateValue = (value: string): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return Number.isNaN(date.getTime()) ? undefined : date;
};
